import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Modal } from '@components/molecules/Modal';
import { FormGroup } from '@components/molecules/form/FormGroup';
import ReactSelect, { SelectOption } from '@components/atoms/ReactSelect';
import { SingleValue, MultiValue } from 'react-select';
import {
  useMaterialCatalog,
  useStockLevels,
  useWarehouses,
  useAdjustStock,
} from '@hooks/api/warehouse';
import {
  MaterialCatalog,
  StockLevel,
  Warehouse,
  AdjustStockInput,
  AdjustmentReason,
} from '@models/warehouse.model';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import TuneIcon from '@mui/icons-material/Tune';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from '@hooks/useTranslation';

type AdjustmentType = 'add' | 'remove';

interface AdjustStockModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialCatalogId?: string | null;
  onSuccess?: () => void;
}

// Sprint 30, story-152. Reads canonical Spanish from the shared `enumLabels` section
// (docs/architecture.md Sprint 30) instead of a hardcoded English label table — the exhaustive/
// catalog-fallback house pattern story-148 established, applied to `AdjustmentReason` for the
// first time (§5b's terms, wired to code here).
const getReasonOptions = (
  tEnumLabels: ReturnType<typeof useTranslation<'enumLabels'>>,
): { value: AdjustmentReason; label: string }[] => [
  { value: AdjustmentReason.COUNT_CORRECTION, label: tEnumLabels.adjustmentReasonCountCorrection },
  { value: AdjustmentReason.DAMAGED, label: tEnumLabels.adjustmentReasonDamaged },
  { value: AdjustmentReason.LOST, label: tEnumLabels.adjustmentReasonLost },
  { value: AdjustmentReason.STOLEN, label: tEnumLabels.adjustmentReasonStolen },
  { value: AdjustmentReason.BROKEN, label: tEnumLabels.adjustmentReasonBroken },
  { value: AdjustmentReason.EXPIRED, label: tEnumLabels.adjustmentReasonExpired },
  { value: AdjustmentReason.FOUND, label: tEnumLabels.adjustmentReasonFound },
  { value: AdjustmentReason.TRANSFER, label: tEnumLabels.adjustmentReasonTransfer },
  { value: AdjustmentReason.OTHER, label: tEnumLabels.adjustmentReasonOther },
];

export const AdjustStockModal: React.FC<AdjustStockModalProps> = ({
  isOpen,
  onClose,
  initialCatalogId,
  onSuccess,
}) => {
  const tWarehouse = useTranslation('warehouse');
  const tEnumLabels = useTranslation('enumLabels');
  const tCommon = useTranslation('common');
  const REASON_OPTIONS = getReasonOptions(tEnumLabels);

  // Form state
  const [selectedMaterial, setSelectedMaterial] = useState<MaterialCatalog | null>(null);
  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse | null>(null);
  const [adjustmentType, setAdjustmentType] = useState<AdjustmentType>('remove');
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showMaterialDropdown, setShowMaterialDropdown] = useState(false);

  // API Hooks
  const { data: materials = [] } = useMaterialCatalog();
  const { data: stockLevels = [] } = useStockLevels();
  const { data: warehouses = [] } = useWarehouses();
  const adjustStockMutation = useAdjustStock();

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedMaterial(null);
      setSelectedWarehouse(null);
      setAdjustmentType('remove');
      setQuantity('');
      setReason('');
      setDetails('');
      setSearchQuery('');
      setShowMaterialDropdown(false);
    }
  }, [isOpen]);

  // Set default warehouse
  useEffect(() => {
    if (isOpen && warehouses.length > 0 && !selectedWarehouse) {
      const defaultWarehouse = warehouses.find((w) => w.isDefault) || warehouses[0];
      setSelectedWarehouse(defaultWarehouse);
    }
  }, [warehouses, selectedWarehouse, isOpen]);

  // Set initial material from prop
  useEffect(() => {
    if (isOpen && initialCatalogId && materials.length > 0 && !selectedMaterial) {
      const material = materials.find((m) => m.catalogId === initialCatalogId);
      if (material) setSelectedMaterial(material);
    }
  }, [initialCatalogId, materials, selectedMaterial, isOpen]);

  // Filter materials by search
  const filteredMaterials = useMemo(() => {
    if (!searchQuery) return materials;
    const query = searchQuery.toLowerCase();
    return materials.filter(
      (m) =>
        m.name.toLowerCase().includes(query) ||
        m.sku?.toLowerCase().includes(query)
    );
  }, [materials, searchQuery]);

  // Get current stock level
  const currentStockLevel = useMemo<StockLevel | undefined>(() => {
    if (!selectedMaterial || !selectedWarehouse) return undefined;
    return stockLevels.find(
      (sl) =>
        sl.catalogId === selectedMaterial.catalogId &&
        sl.warehouseId === selectedWarehouse.warehouseId
    );
  }, [stockLevels, selectedMaterial, selectedWarehouse]);

  // Calculate preview
  const preview = useMemo(() => {
    const qty = parseFloat(quantity) || 0;
    const currentQty = currentStockLevel?.quantityOnHand || 0;
    const adjustedQty = adjustmentType === 'add' ? qty : -qty;
    const afterQty = currentQty + adjustedQty;
    const reorderPoint = selectedMaterial?.reorderPoint || 0;
    const isBelowReorder = afterQty < reorderPoint && reorderPoint > 0;

    return {
      currentQty,
      afterQty: Math.max(afterQty, 0),
      adjustedQty,
      reorderPoint,
      isBelowReorder,
      isNegative: afterQty < 0,
    };
  }, [quantity, adjustmentType, currentStockLevel, selectedMaterial]);

  // Handlers
  const handleSelectMaterial = useCallback((material: MaterialCatalog) => {
    setSelectedMaterial(material);
    setSearchQuery('');
    setShowMaterialDropdown(false);
  }, []);

  const handleClearMaterial = useCallback(() => {
    setSelectedMaterial(null);
    setSearchQuery('');
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!selectedMaterial || !selectedWarehouse || !quantity || !details.trim()) {
      return;
    }

    const qty = parseFloat(quantity);
    const currentQty = currentStockLevel?.quantityOnHand || 0;
    const newQuantity = adjustmentType === 'add' ? currentQty + qty : currentQty - qty;

    const input: AdjustStockInput = {
      catalogId: selectedMaterial.catalogId,
      warehouseId: selectedWarehouse.warehouseId,
      newQuantity,
      reason: reason ? (reason as AdjustmentReason) : undefined,
      notes: details.trim(),
    };

    try {
      await adjustStockMutation.mutateAsync(input);
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error adjusting stock:', error);
    }
  }, [
    selectedMaterial,
    selectedWarehouse,
    quantity,
    adjustmentType,
    reason,
    details,
    adjustStockMutation,
    onSuccess,
    onClose,
    currentStockLevel,
  ]);

  const isFormValid = selectedMaterial && selectedWarehouse && quantity && details.trim() && !preview.isNegative;

  // Stock status for selected material.
  // Sprint 30, story-152 review Critical #1. Previously a plain conditional-return helper
  // (invisible to `hardcoded-label-table`/`hardcoded-jsx-text`/`hardcoded-prop` alike) returning
  // raw English literals — the catalog already had all three tiers under `enumLabels` (used by
  // the badge-atom family, story-148), just never wired up here.
  const stockStatus = useMemo(() => {
    if (!selectedMaterial || !currentStockLevel) return null;
    const qty = currentStockLevel.quantityOnHand;
    const reorder = selectedMaterial.reorderPoint || 0;
    if (qty === 0) {
      return { tier: 'out' as const, label: tEnumLabels.stockStatusOutOfStock, color: 'bg-red-100 text-red-700' };
    }
    if (reorder > 0 && qty <= reorder) {
      return { tier: 'low' as const, label: tEnumLabels.stockStatusLowStock, color: 'bg-amber-100 text-amber-700' };
    }
    return { tier: 'in' as const, label: tEnumLabels.stockStatusInStock, color: 'bg-green-100 text-green-700' };
  }, [selectedMaterial, currentStockLevel, tEnumLabels]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={tWarehouse.adjustment.title}
      description={tWarehouse.adjustment.description}
      size="large"
      handleConfirm={handleSubmit}
      confirmText={
        adjustStockMutation.isPending ? tWarehouse.adjustment.applying : tWarehouse.adjustment.applyAdjustment
      }
      cancelText={tCommon.cancel}
      isLoading={adjustStockMutation.isPending}
      disableConfirm={!isFormValid || adjustStockMutation.isPending}
    >
      <div className="space-y-6">
        {/* Warning Banner */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <WarningAmberIcon className="text-amber-500 mt-0.5" sx={{ fontSize: 18 }} />
            <p className="text-sm text-amber-700">
              {tWarehouse.adjustment.warningIntro}{' '}
              <strong>{tWarehouse.receiveStock}</strong> {tWarehouse.adjustment.warningMiddle}{' '}
              <strong>{tWarehouse.adjustment.taskConsumptionLink}</strong> {tWarehouse.adjustment.warningEnd}
            </p>
          </div>
        </div>

        {/* Material Selection */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Inventory2Icon className="text-amber-600" sx={{ fontSize: 18 }} />
            {tWarehouse.materialSelection}
          </h3>

          {!selectedMaterial ? (
            <div className="relative">
              <FormGroup label={tWarehouse.selectMaterialRequired} htmlFor="adjustMaterialSearch">
                <div className="relative">
                  <SearchIcon
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                    sx={{ fontSize: 18 }}
                  />
                  <input
                    id="adjustMaterialSearch"
                    type="text"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setShowMaterialDropdown(true);
                    }}
                    onFocus={() => setShowMaterialDropdown(true)}
                    placeholder={tWarehouse.searchOrSelectMaterial}
                    className="w-full border border-gray-300 rounded-md py-2.5 pl-10 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  />
                </div>
              </FormGroup>

              {showMaterialDropdown && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {filteredMaterials.length === 0 ? (
                    <div className="p-4 text-center text-gray-500 text-sm">
                      {tWarehouse.noMaterialsFound}
                    </div>
                  ) : (
                    filteredMaterials.map((material) => (
                      <button type="button"
                        key={material.catalogId}
                        onClick={() => handleSelectMaterial(material)}
                        className="w-full px-4 py-2.5 text-left hover:bg-gray-50 border-b border-gray-100 last:border-0"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-7 h-7 bg-gray-100 rounded flex items-center justify-center">
                            <Inventory2Icon className="text-gray-600" sx={{ fontSize: 14 }} />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">{material.name}</p>
                            <p className="text-xs text-gray-500">
                              {material.sku || 'No SKU'}{' '}
                              {material.category && `- ${material.category.name}`}
                            </p>
                          </div>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Inventory2Icon className="text-amber-600" sx={{ fontSize: 20 }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-semibold text-gray-900 text-sm">{selectedMaterial.name}</h4>
                      <p className="text-xs text-gray-500">
                        {selectedMaterial.sku || 'No SKU'}
                        {selectedMaterial.category && ` - ${selectedMaterial.category.name}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {stockStatus && (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${stockStatus.color}`}>
                          {stockStatus.label}
                        </span>
                      )}
                      <button type="button" onClick={handleClearMaterial} className="text-gray-400 hover:text-gray-600">
                        <CloseIcon sx={{ fontSize: 16 }} />
                      </button>
                    </div>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <p className="text-gray-500">{tWarehouse.currentStock}</p>
                      <p className={`font-semibold ${
                        stockStatus?.tier === 'low' ? 'text-amber-600' :
                        stockStatus?.tier === 'out' ? 'text-red-600' : 'text-gray-900'
                      }`}>
                        {currentStockLevel?.quantityOnHand || 0} {selectedMaterial.unit || tWarehouse.genericUnitPlural}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">{tWarehouse.reorderPoint}</p>
                      <p className="font-semibold text-gray-900">
                        {selectedMaterial.reorderPoint || 0} {selectedMaterial.unit || tWarehouse.genericUnitPlural}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Warehouse Selection */}
          {warehouses.length > 1 && (
            <div className="mt-3">
              <FormGroup label={tWarehouse.warehouseRequired} htmlFor="adjustWarehouseSelect">
                <ReactSelect
                  options={warehouses.map((wh) => ({
                    value: wh.warehouseId,
                    label: `${wh.name}${wh.isDefault ? ' (Default)' : ''}`,
                  }))}
                  value={
                    selectedWarehouse
                      ? {
                          value: selectedWarehouse.warehouseId,
                          label: `${selectedWarehouse.name}${selectedWarehouse.isDefault ? ' (Default)' : ''}`,
                        }
                      : null
                  }
                  onChange={(option: SingleValue<SelectOption> | MultiValue<SelectOption>) => {
                    if (option && !Array.isArray(option) && 'value' in option) {
                      const wh = warehouses.find((w) => w.warehouseId === option.value);
                      setSelectedWarehouse(wh || null);
                    }
                  }}
                  placeholder={tWarehouse.selectWarehousePlaceholder}
                />
              </FormGroup>
            </div>
          )}
        </div>

        {/* Adjustment Details */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <TuneIcon className="text-amber-600" sx={{ fontSize: 18 }} />
            {tWarehouse.adjustment.details}
          </h3>

          {/* Adjustment Type */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">{tWarehouse.adjustment.typeLabel}</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setAdjustmentType('add')}
                className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                  adjustmentType === 'add'
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <AddCircleOutlineIcon
                  className={adjustmentType === 'add' ? 'text-green-600' : 'text-gray-400'}
                  sx={{ fontSize: 24 }}
                />
                <div className="text-left">
                  <p className={`text-sm font-medium ${
                    adjustmentType === 'add' ? 'text-green-700' : 'text-gray-700'
                  }`}>
                    {tWarehouse.adjustment.addToStock}
                  </p>
                  <p className="text-xs text-gray-500">{tWarehouse.foundExtraInventory}</p>
                </div>
              </button>
              <button
                type="button"
                onClick={() => setAdjustmentType('remove')}
                className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                  adjustmentType === 'remove'
                    ? 'border-red-500 bg-red-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <RemoveCircleOutlineIcon
                  className={adjustmentType === 'remove' ? 'text-red-600' : 'text-gray-400'}
                  sx={{ fontSize: 24 }}
                />
                <div className="text-left">
                  <p className={`text-sm font-medium ${
                    adjustmentType === 'remove' ? 'text-red-700' : 'text-gray-700'
                  }`}>
                    {tWarehouse.adjustment.removeFromStock}
                  </p>
                  <p className="text-xs text-gray-500">{tWarehouse.damagedLostStolen}</p>
                </div>
              </button>
            </div>
          </div>

          {/* Quantity */}
          <FormGroup label={tWarehouse.quantityRequired} htmlFor="adjustQuantity">
            <div className="flex">
              <input
                id="adjustQuantity"
                type="number"
                min="0"
                step="1"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder="0"
                className="flex-1 border border-gray-300 rounded-l-md py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              />
              <span className="inline-flex items-center px-3 py-2 border border-l-0 border-gray-300 rounded-r-md bg-gray-50 text-gray-500 text-sm">
                {selectedMaterial?.unit || tWarehouse.genericUnitPlural}
              </span>
            </div>
            {preview.isNegative && (
              <p className="mt-1 text-xs text-red-600">
                {tWarehouse.adjustment.quantityExceedsStock} {preview.currentQty} {selectedMaterial?.unit || tWarehouse.genericUnitPlural}
              </p>
            )}
          </FormGroup>

          {/* Reason */}
          <div className="mt-4">
            <FormGroup label={tWarehouse.adjustment.reasonForAdjustment} htmlFor="adjustReason">
              <ReactSelect
                options={REASON_OPTIONS.map((opt) => ({
                  value: opt.value,
                  label: opt.label,
                }))}
                value={
                  reason
                    ? {
                        value: reason,
                        label: REASON_OPTIONS.find((r) => r.value === reason)?.label || reason,
                      }
                    : null
                }
                onChange={(option: SingleValue<SelectOption> | MultiValue<SelectOption>) => {
                  if (option && !Array.isArray(option) && 'value' in option) {
                    setReason(option.value as string);
                  } else {
                    setReason('');
                  }
                }}
                placeholder={tWarehouse.adjustment.selectReasonPlaceholder}
                isClearable
              />
            </FormGroup>
          </div>

          {/* Details */}
          <div className="mt-4">
            <FormGroup label={tWarehouse.detailsRequired} htmlFor="adjustDetails">
              <textarea
                id="adjustDetails"
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                placeholder={tWarehouse.adjustment.describeReasonPlaceholder}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 resize-none text-sm"
                rows={3}
              />
              <p className="mt-1 text-xs text-gray-500">{tWarehouse.adjustment.noteAuditPurposes}</p>
            </FormGroup>
          </div>
        </div>

        {/* Result Preview */}
        {selectedMaterial && quantity && parseFloat(quantity) > 0 && (
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">{tWarehouse.resultPreview}</h3>

            {/* Visual calculation */}
            <div className="flex items-center justify-center gap-4 py-4">
              <div className="text-center">
                <p className="text-xs text-gray-500 mb-1">{tWarehouse.currentStock}</p>
                <p className="text-2xl font-bold text-gray-900">
                  {preview.currentQty}
                </p>
                <p className="text-xs text-gray-500">{selectedMaterial.unit || tWarehouse.genericUnitPlural}</p>
              </div>

              <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                adjustmentType === 'add' ? 'bg-green-100' : 'bg-red-100'
              }`}>
                <ArrowForwardIcon
                  className={adjustmentType === 'add' ? 'text-green-600' : 'text-red-600'}
                  sx={{ fontSize: 18 }}
                />
              </div>

              <div className="text-center">
                <p className="text-xs text-gray-500 mb-1">{tWarehouse.afterAdjustment}</p>
                <p className={`text-2xl font-bold ${
                  adjustmentType === 'add' ? 'text-green-600' : 'text-red-600'
                }`}>
                  {preview.afterQty}
                </p>
                <p className="text-xs text-gray-500">{selectedMaterial.unit || tWarehouse.genericUnitPlural}</p>
              </div>
            </div>

            {/* Summary line */}
            <div className="text-center text-sm text-gray-600 mb-3">
              {adjustmentType === 'add' ? (
                <span className="flex items-center justify-center gap-1 text-green-600">
                  <AddCircleOutlineIcon sx={{ fontSize: 16 }} />
                  {tWarehouse.adjustment.adding} {quantity} {selectedMaterial.unit || tWarehouse.genericUnitPlural} {tWarehouse.adjustment.toStock}
                </span>
              ) : (
                <span className="flex items-center justify-center gap-1 text-red-600">
                  <RemoveCircleOutlineIcon sx={{ fontSize: 16 }} />
                  {tWarehouse.adjustment.removing} {quantity} {selectedMaterial.unit || tWarehouse.genericUnitPlural} {tWarehouse.adjustment.fromStock}
                </span>
              )}
            </div>

            {/* Low stock warning */}
            {preview.isBelowReorder && !preview.isNegative && (
              <div className="p-3 bg-red-50 rounded-md border border-red-200">
                <div className="flex items-start gap-2">
                  <WarningAmberIcon className="text-red-500 mt-0.5" sx={{ fontSize: 16 }} />
                  <div className="text-sm text-red-700">
                    <p className="font-medium">{tWarehouse.criticalLowStockWarning}</p>
                    <p className="mt-1">
                      {tWarehouse.adjustment.afterAdjustmentStockAt} {preview.afterQty} {selectedMaterial.unit || tWarehouse.genericUnitPlural},
                      {' '}{tWarehouse.adjustment.belowReorderPointOf} {preview.reorderPoint} {selectedMaterial.unit || tWarehouse.genericUnitPlural}.
                      {' '}{tWarehouse.adjustment.considerOrderingMore}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Negative stock warning */}
            {preview.isNegative && (
              <div className="p-3 bg-red-50 rounded-md border border-red-200">
                <div className="flex items-start gap-2">
                  <InfoOutlinedIcon className="text-red-500 mt-0.5" sx={{ fontSize: 16 }} />
                  <p className="text-sm text-red-700">
                    {tWarehouse.adjustment.cannotRemoveMoreThanStock}{preview.currentQty} {selectedMaterial.unit || tWarehouse.genericUnitPlural}).
                    {' '}{tWarehouse.adjustment.pleaseReduceQuantity}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};
